import type { Vec2 } from "./types.ts";
import type { Pathfinding, PathGrid } from "./Pathfinding.ts";
import { CELL_SIZE } from "./config.ts";

interface ManagedPath {
  id: string;
  entry: Vec2;
  exit: Vec2;
  pathGrid: PathGrid;
  cached: Vec2[] | null;
  pixelCached: Vec2[] | null;
}

export class PathRegistry {
  private paths = new Map<string, ManagedPath>();
  private allPixelPathsCache: Vec2[][] = [];
  private allPixelPathsDirty = true;

  constructor(private pathfinding: Pathfinding) {}

  register(id: string, entry: Vec2, exit: Vec2, pathGrid: PathGrid): void {
    this.paths.set(id, {
      id,
      entry,
      exit,
      pathGrid,
      cached: null,
      pixelCached: null,
    });
    this.allPixelPathsDirty = true;
    this.recalculate(id);
  }

  unregister(id: string): void {
    this.paths.delete(id);
    this.allPixelPathsDirty = true;
  }

  recalculateAll(): void {
    for (const id of this.paths.keys()) this.recalculate(id);
  }

  getPixelPath(id: string): Vec2[] | null {
    return this.paths.get(id)?.pixelCached ?? null;
  }

  getAllPixelPaths(): Vec2[][] {
    if (!this.allPixelPathsDirty) {
      return this.allPixelPathsCache;
    }
    const next: Vec2[][] = [];
    for (const p of this.paths.values()) {
      if (p.pixelCached) next.push(p.pixelCached);
    }
    this.allPixelPathsCache = next;
    this.allPixelPathsDirty = false;
    return this.allPixelPathsCache;
  }

  /** CR-H6: id-paired iterator so consumers (WaveManager spawnEnemy,
   *  rerouteActiveEnemies) can attribute paths to enemies via spawnPathId. */
  getAllPathEntries(): Array<{ id: string; pixels: Vec2[] }> {
    const out: Array<{ id: string; pixels: Vec2[] }> = [];
    for (const p of this.paths.values()) {
      if (p.pixelCached) out.push({ id: p.id, pixels: p.pixelCached });
    }
    return out;
  }

  findPixelPathFrom(id: string, startPixel: Vec2): Vec2[] | null {
    const p = this.paths.get(id);
    if (!p) return null;

    const start = {
      x: Math.max(
        0,
        Math.min(
          p.pathGrid.width - 1,
          Math.round((startPixel.x - CELL_SIZE / 2) / CELL_SIZE),
        ),
      ),
      y: Math.max(
        0,
        Math.min(
          p.pathGrid.height - 1,
          Math.round((startPixel.y - CELL_SIZE / 2) / CELL_SIZE),
        ),
      ),
    };

    if (p.pathGrid.isBlocked(start.x, start.y)) return null;

    const gridPath = this.pathfinding.findPathUncached(
      p.pathGrid,
      start,
      p.exit,
    );
    if (!gridPath) return null;

    return gridPath.map((v) => ({
      x: v.x * CELL_SIZE + CELL_SIZE / 2,
      y: v.y * CELL_SIZE + CELL_SIZE / 2,
    }));
  }

  /** Bug 12: a render-only "what-if" preview of a registered path's route when
   *  an extra cell (blockX,blockY) is hypothetically blocked — exactly mirrors
   *  the route a placement at that cell would force (same pathGrid + A*). Uncached
   *  and side-effect-free: it never mutates registry/grid state or the cache, so
   *  it is determinism-neutral (pure display). Returns null if no route survives. */
  previewPixelPathWithBlock(
    id: string,
    blockX: number,
    blockY: number,
  ): Vec2[] | null {
    const p = this.paths.get(id);
    if (!p) return null;
    return this.computePixelPathWithBlock(p, blockX, blockY);
  }

  /** LD-22.3 (Bug: 2nd-spawn preview missing): the multi-path generalization of
   *  previewPixelPathWithBlock — the what-if route for EVERY registered path when
   *  (blockX,blockY) is hypothetically blocked. Returned in registry order
   *  (primary first), so it mirrors getAllPixelPaths() and keeps the renderer's
   *  index-0-is-primary styling aligned between the committed and preview lines.
   *  Paths the block would strand (no surviving route, or a degenerate <2-waypoint
   *  route) are omitted. Pure display: no registry/grid/cache mutation.
   *
   *  Why this exists: tower placement reroutes ALL active paths (rerouteAllPaths),
   *  but the hover preview previously only recomputed "primary" — so secondary
   *  (breach) spawn routes showed no preview until AFTER a tower was committed. */
  previewAllPixelPathsWithBlock(blockX: number, blockY: number): Vec2[][] {
    const out: Vec2[][] = [];
    for (const p of this.paths.values()) {
      const pixels = this.computePixelPathWithBlock(p, blockX, blockY);
      if (pixels && pixels.length > 1) out.push(pixels);
    }
    return out;
  }

  /** Shared core for the what-if previews: A* from a path's entry to its exit over
   *  a hypothetical overlay that blocks (blockX,blockY) on top of the path's live
   *  grid. Uncached and side-effect-free. Returns null if no route survives. */
  private computePixelPathWithBlock(
    p: ManagedPath,
    blockX: number,
    blockY: number,
  ): Vec2[] | null {
    const overlay: PathGrid = {
      width: p.pathGrid.width,
      height: p.pathGrid.height,
      isBlocked: (gx, gy) =>
        (gx === blockX && gy === blockY) || p.pathGrid.isBlocked(gx, gy),
    };
    const gridPath = this.pathfinding.findPathUncached(overlay, p.entry, p.exit);
    if (!gridPath) return null;
    return gridPath.map((v) => ({
      x: v.x * CELL_SIZE + CELL_SIZE / 2,
      y: v.y * CELL_SIZE + CELL_SIZE / 2,
    }));
  }

  clear(): void {
    this.paths.clear();
    this.allPixelPathsCache = [];
    this.allPixelPathsDirty = false;
  }

  has(id: string): boolean {
    return this.paths.has(id);
  }

  private recalculate(id: string): void {
    const p = this.paths.get(id);
    if (!p) return;
    // PT-03: a recalculation must recompute against the live grid. The shared
    // Pathfinding cache is keyed only on start->end, so a grid mutation that
    // leaves the primary's entry/exit unchanged would otherwise return the
    // stale pre-mutation route — making freshly spawned enemies deviate from
    // the rendered path. Always recompute uncached here; the cache stays a
    // read-side optimization for callers that key on a stable grid.
    const cached = this.pathfinding.findPathUncached(
      p.pathGrid,
      p.entry,
      p.exit,
    );
    p.cached = cached;
    p.pixelCached = cached
      ? cached.map((v) => ({
          x: v.x * CELL_SIZE + CELL_SIZE / 2,
          y: v.y * CELL_SIZE + CELL_SIZE / 2,
        }))
      : null;
    this.allPixelPathsDirty = true;
  }
}
